/**
 * Activity Types Store - Pinia
 *
 * Manages activity type selection and list.
 * Migrated from VueX module src/store/modules/activityTypes/
 *
 * Note: Has cross-store dependency to settings store for persistence.
 * Note: Has dependency to settingsPresets store for cascade delete.
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import GeoMoverDB from '@/db/geoMoverDB';
import { DEFAULT_COLOR } from '@/utils/colorConstants';
import { useColorPalettesStore } from './colorPalettes';

/**
 * Color variants for same-type tracks (Tailwind-style lighter shades)
 * Used when multiple tracks of the same activity type are displayed
 */
const COLOR_VARIANTS = {
  '#ef4444': ['#ef4444', '#f87171', '#fca5a5'], // red: 500, 400, 300
  '#f97316': ['#f97316', '#fb923c', '#fdba74'], // orange: 500, 400, 300
  '#eab308': ['#eab308', '#facc15', '#fde047'], // yellow: 500, 400, 300
  '#22c55e': ['#22c55e', '#4ade80', '#86efac'], // green: 500, 400, 300
  '#06b6d4': ['#06b6d4', '#22d3ee', '#67e8f9'], // cyan: 500, 400, 300
  '#3b82f6': ['#3b82f6', '#60a5fa', '#93c5fd'], // blue: 500, 400, 300
  '#8b5cf6': ['#8b5cf6', '#a78bfa', '#c4b5fd'], // violet: 500, 400, 300
  '#6b7280': ['#6b7280', '#9ca3af', '#d1d5db'], // gray: 500, 400, 300
  '#ec4899': ['#ec4899', '#f472b6', '#f9a8d4'] // pink (default): 500, 400, 300
};

export const useActivityTypesStore = defineStore('activityTypes', {
  state: () => ({
    activityTypes: [],
    currentActivityType: '',
    currentActivityTypeObj: null
  }),

  getters: {
    getAllActivityTypes: (state) => state.activityTypes,
    getCurrentActivityType: (state) => state.currentActivityType,
    getCurrentActivityTypeObj: (state) => state.currentActivityTypeObj,

    /**
     * Get the full color palette from colorPalettes store
     * @returns {Array} Array of color objects with id, hex, name
     */
    getColorPalette () {
      const colorPalettesStore = useColorPalettesStore();
      return colorPalettesStore.getActiveColors;
    },

    /**
     * Get hex colors currently in use by activity types
     * @returns {Array<string>} Array of hex color codes in use
     */
    getUsedColors () {
      const colorPalettesStore = useColorPalettesStore();
      return this.activityTypes
        .map(a => a.colorId ? colorPalettesStore.getHexById(a.colorId) : null)
        .filter(Boolean);
    },

    /**
     * Get color IDs currently in use by activity types
     * @returns {Array<number>} Array of color IDs in use
     */
    getUsedColorIds: (state) => {
      return state.activityTypes
        .map(a => a.colorId)
        .filter(id => id != null);
    },

    /**
     * Get colors not yet assigned to any activity type
     * @returns {Array} Array of color objects not in use
     */
    getUnusedColors () {
      const usedColorIds = this.getUsedColorIds;
      return this.getColorPalette.filter(c => !usedColorIds.includes(c.id));
    },

    /**
     * Get color for an activity type by title
     * Uses colorId to look up hex color from the active palette
     * @param {string} typeTitle - The activity type title
     * @returns {string} The hex color code
     */
    getColorByType () {
      const colorPalettesStore = useColorPalettesStore();
      return (typeTitle) => {
        const activityType = this.activityTypes.find(a => a.title === typeTitle);
        if (activityType?.colorId) {
          return colorPalettesStore.getHexById(activityType.colorId);
        }
        // Fallback to default color
        return DEFAULT_COLOR;
      };
    },

    /**
     * Get color variant for an activity type (for same-type duplicates in merge view)
     * @param {string} typeTitle - The activity type title
     * @param {number} variantIndex - 0 for base, 1 for lighter, 2 for lightest
     * @returns {string} The hex color code for the variant
     */
    getColorVariant () {
      return (typeTitle, variantIndex = 0) => {
        const baseColor = this.getColorByType(typeTitle);

        // Get color variants, fallback to pink variants, or generate single-color array
        const variants = COLOR_VARIANTS[baseColor]
          || COLOR_VARIANTS['#ec4899']  // pink fallback
          || [baseColor, baseColor, baseColor];  // ultimate fallback: same color 3x

        const clampedIndex = Math.min(variantIndex, variants.length - 1);
        return variants[clampedIndex];
      };
    },

    /**
     * Find the "Mixed" activity type (for merged tracks)
     * @returns {Object|null} The Mixed activity type or null
     */
    getMixedActivityType: (state) => {
      return state.activityTypes.find(a => a.title === 'Mixed') || null;
    },

    /**
     * Get icon name for an activity type by title
     * @param {string} typeTitle - The activity type title
     * @returns {string|null} The icon name or null
     */
    getIconByType: (state) => (typeTitle) => {
      const activityType = state.activityTypes.find(a => a.title === typeTitle);
      return activityType?.iconName || null;
    }
  },

  actions: {
    /**
     * Load activity types from database
     * Colors are looked up by colorId at display time via getColorByType()
     * @param {Array} payload - Array of activity type objects from DB
     */
    loadActivityTypes (payload) {
      this.activityTypes = payload;
    },

    /**
     * Set current activity type and persist to settings
     * @param {string} payload - Activity type ID
     */
    setCurrentActivityType (payload) {
      this.currentActivityType = payload;
      // Cross-store call to settings - will be connected after settings store is migrated
      // For now, import and use settings store when available
      try {
        const { useSettingsStore } = require('./settings');
        const settingsStore = useSettingsStore();
        settingsStore.updateOneSetting({ feature: 'currentActivityType', value: payload });
      }
      catch {
        // Settings store not yet migrated, VueX will handle this
        appDebugLog.debug('[Pinia] Settings store not yet available for cross-store call');
      }
    },

    setCurrentActivityTypeObj (payload) {
      this.currentActivityTypeObj = payload;
    },

    /**
     * Add a new activity type with auto-created default preset
     * @param {string} title - The activity type title
     * @param {number|null} colorId - Optional color ID (defaults to 18 = Pink)
     * @returns {Promise<number|null>} The new activity type ID
     */
    async addActivityType (title, colorId = null) {
      try {
        // Use provided colorId or default to 18 (Pink)
        const activityColorId = colorId ?? 18;

        // Add to database with colorId only
        const newId = await GeoMoverDB.addOne('activityTypes', { title, colorId: activityColorId });

        if (typeof newId === 'number') {
          // Add to local state with colorId
          this.activityTypes = [...this.activityTypes, { id: newId, title, colorId: activityColorId }];

          // Auto-create default preset for this activity
          try {
            const { useSettingsPresetsStore } = require('./settingsPresets');
            const presetsStore = useSettingsPresetsStore();
            await presetsStore.createDefaultPresetForActivity(newId, title);
          }
          catch (presetError) {
            appDebugLog.warn('[ActivityTypes] Could not create default preset:', presetError);
          }

          appDebugLog.log('[ActivityTypes] Added activity type:', title, 'ID:', newId);
          return newId;
        }
        else {
          appDebugLog.error('[ActivityTypes] Failed to add activity type:', newId);
          return null;
        }
      }
      catch (error) {
        appDebugLog.error('[ActivityTypes] Error adding activity type:', error);
        return null;
      }
    },

    /**
     * Delete an activity type with cascade delete of related presets
     * @param {number} activityTypeId - The activity type ID to delete
     * @returns {Promise<boolean>} True if successful
     */
    async deleteActivityType (activityTypeId) {
      try {
        // First, cascade delete all presets for this activity
        try {
          const { useSettingsPresetsStore } = require('./settingsPresets');
          const presetsStore = useSettingsPresetsStore();
          const deletedCount = await presetsStore.deletePresetsForActivityType(activityTypeId);
          appDebugLog.log('[ActivityTypes] Cascade deleted', deletedCount, 'presets');
        }
        catch (presetError) {
          appDebugLog.warn('[ActivityTypes] Could not cascade delete presets:', presetError);
        }

        // Now delete the activity type
        await GeoMoverDB.deleteOne('activityTypes', activityTypeId);

        // Update local state
        this.activityTypes = this.activityTypes.filter(a => a.id !== activityTypeId);

        // If this was the current activity, clear it
        if (this.currentActivityType === activityTypeId ||
            this.currentActivityTypeObj?.id === activityTypeId) {
          this.currentActivityType = '';
          this.currentActivityTypeObj = null;
        }

        appDebugLog.log('[ActivityTypes] Deleted activity type:', activityTypeId);
        return true;
      }
      catch (error) {
        appDebugLog.error('[ActivityTypes] Error deleting activity type:', error);
        return false;
      }
    },

    /**
     * Update the color ID of an existing activity type
     * @param {number} activityTypeId - The activity type ID to update
     * @param {number} newColorId - The new color ID
     * @returns {Promise<boolean>} True if successful
     */
    async updateActivityTypeColorId (activityTypeId, newColorId) {
      try {
        // Find the activity type
        const activityType = this.activityTypes.find(a => a.id === activityTypeId);
        if (!activityType) {
          appDebugLog.error('[ActivityTypes] Activity type not found:', activityTypeId);
          return false;
        }

        // Update in database - only colorId
        await GeoMoverDB.updateOne('activityTypes', {
          ...activityType,
          colorId: newColorId
        });

        // Update local state
        this.activityTypes = this.activityTypes.map(a =>
          a.id === activityTypeId ? { ...a, colorId: newColorId } : a
        );

        appDebugLog.log('[ActivityTypes] Updated colorId for', activityType.title, 'to', newColorId);
        return true;
      }
      catch (error) {
        appDebugLog.error('[ActivityTypes] Error updating colorId:', error);
        return false;
      }
    },

    /**
     * Reload activity types from database
     */
    async reloadActivityTypes () {
      try {
        const types = await GeoMoverDB.findAll('activityTypes');
        this.activityTypes = types;
        return types;
      }
      catch (error) {
        appDebugLog.error('[ActivityTypes] Error reloading:', error);
        return [];
      }
    }
  }
});
